import React from "react";
import { api, GALLERY_CATEGORIES, fileUrl } from "@/lib/config";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Upload, Loader2, Trash2 } from "lucide-react";

export default function GalleryAdmin() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState({ title: "", category: "", description: "", image_path: "" });
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data.items));
  React.useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: r.data.path }));
      setPreviewUrl(URL.createObjectURL(file));
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error("Please upload an image."); return; }
    if (!form.category) { toast.error("Please choose a category."); return; }
    setSaving(true);
    try {
      await api.post("/admin/gallery", form);
      toast.success("Added to gallery.");
      setForm({ title: "", category: "", description: "", image_path: "" });
      setPreviewUrl("");
      load();
    } catch { toast.error("Failed to add."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  return (
    <div data-testid="admin-gallery" className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={add} className="lg:col-span-4 rounded-2xl border border-black/10 bg-white p-6 h-fit space-y-4" data-testid="gallery-add-form">
        <div className="eyebrow text-[color:var(--brand-wood)]">Add photo</div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Title</Label>
          <Input required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="gallery-title"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Category</Label>
          <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
            <SelectTrigger data-testid="gallery-category"><SelectValue placeholder="Pick a category"/></SelectTrigger>
            <SelectContent>
              {GALLERY_CATEGORIES.map((c)=><SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Description</Label>
          <Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="gallery-description"/>
        </div>
        <div>
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
            {uploading ? "Uploading..." : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="gallery-file"/>
          </label>
          {previewUrl && <img src={previewUrl} alt="preview" className="mt-3 rounded-xl aspect-video object-cover w-full"/>}
        </div>
        <button type="submit" disabled={saving} data-testid="gallery-save"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-2.5 text-sm disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
          Add to gallery
        </button>
      </form>

      <div className="lg:col-span-8">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/10 p-10 text-center text-sm text-[color:var(--brand-woodDark)]">
            No gallery photos yet. Add your first project on the left.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`gal-item-${it.id}`} className="group relative rounded-xl overflow-hidden aspect-[4/3]">
                <img src={fileUrl(it.image_path)} alt={it.title} className="w-full h-full object-cover"/>
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white">
                  <div className="text-xs font-medium">{it.title}</div>
                  <div className="text-[10px] opacity-80 uppercase tracking-widest">{it.category?.replace("_", " ")}</div>
                </div>
                <button onClick={()=>remove(it.id)} data-testid={`gal-delete-${it.id}`}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/95 text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="h-4 w-4"/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
